/**
 * <cc-cart-inventory-indicator>
 *
 * Self-contained cart-line inventory indicator. Each instance owns:
 *   - Fetching inventory for its variant from /apps/proxy
 *   - Rendering a line-level message (silent / insufficient / online-only)
 *   - Auto-removing itself if the variant has 0 stock everywhere
 *
 * A module-level CartInventoryController aggregates state across all
 * instances to detect cart-level conflicts and toggle checkout buttons.
 *
 * Checkout is never blocked silently: if a line's inventory can't be loaded
 * (proxy error or timeout, after retries) or its auto-removal fails, the line
 * becomes 'unverified' and stops blocking — the same fail-open behaviour as
 * the PDP indicator. Shopify checkout still validates real stock.
 *
 * Data is provided exclusively via HTML attributes:
 *   data-product-id        Shopify product ID (numeric or GID)
 *   data-variant-id        Shopify variant ID (numeric or GID)
 *   data-cart-quantity     Current cart quantity for this line
 *   data-line-location-id  Location/GID stamped onto the line at add time
 *                          (read from the hidden _cc_location_id property).
 *                          Takes precedence over the currently-selected store
 *                          so quantity checks reflect the store the customer
 *                          was browsing when they added the item.
 *
 * On add-to-cart, a capture-phase submit/click listener appends a hidden
 * `properties[_cc_location_id]` input to the form, and cc-utils' addToCart()
 * embeds the same property in its JSON payload, so every new line remembers
 * its origin store without monkey-patching window.fetch. If the variant is
 * already in the cart, the stamp matches that existing line's value instead
 * (via cc-utils' getExistingLineLocation) so Shopify merges the quantities
 * into one line rather than forking a second line for the same variant.
 *
 * All user-facing copy is read from the cc-store-selector i18n JSON.
 */

import {
  fetchInventory,
  extractLocations,
  isSameLocation,
  isWarehouseLocation as isWarehouseLocationUtil,
  isCountryAllowed,
  escapeHtml,
  getExistingLineLocation,
  onCartLoaded,
  STORAGE_KEY,
  hasLocalPickup,
} from '@cc/utils';

const CART_BANNER_SELECTOR = '[data-cc-cart-conflict-message]';
const CART_REMOVED_BANNER_SELECTOR = '[data-cc-cart-removed-warning]';
const CHECKOUT_SELECTORS = '#checkout, #CartDrawer-Checkout, [data-checkout-button]';
const CART_FORM_SELECTORS = '#cart, #CartDrawer-Form, form[action$="/cart"]';
const HTML_BLOCK_CLASS = 'cc-cart-checkout-blocked';
const LINE_LABEL_CLASS = 'cc-cart-line-label';
const LINE_LOCATION_PROPERTY = '_cc_location_id';
const LINE_LOCATION_INPUT_NAME = `properties[${LINE_LOCATION_PROPERTY}]`;
// A failed inventory fetch is retried after each of these delays before the
// line gives up and stops blocking checkout.
const INVENTORY_RETRY_DELAYS_MS = [1000, 3000];
// Upper bound on how long a line may block checkout while its inventory is
// still loading (e.g. a hanging proxy request).
const INVENTORY_LOAD_TIMEOUT_MS = 10000;

// Module-level serial queue. /cart/change.js mutations from autoRemove and
// autoReduceQuantity must execute one at a time to avoid races against the
// cart-drawer re-render and Shopify rate limits when several lines become
// unfulfillable on a single store change.
let cartMutationQueue = Promise.resolve();
function queueCartMutation(task) {
  const next = cartMutationQueue.then(task, task);
  // Swallow rejection on the chain so one failure doesn't break subsequent
  // mutations, but still surface the error to the original caller.
  cartMutationQueue = next.catch(() => {});
  return next;
}

// ---------------------------------------------------------------------------
// Duplicate-line consolidation — merges cart lines that share a variant but
// ended up split across two lines (carts from before the add-to-cart
// stamping fix below, or any other source of a properties mismatch). Runs
// on every cart load, including the first one on page load, so a customer
// never sees a stale duplicate left over from an earlier visit.
// ---------------------------------------------------------------------------
let _consolidating = false;

onCartLoaded((items) => consolidateDuplicateVariants(items));

// Other features (e.g. gift-card-recipient-form.liquid) also stamp custom
// line-item properties, and two lines of the same variant can legitimately
// carry different values for those (two gift cards, two recipients) — that
// split is intentional and must survive. Only fold two lines together when
// they're identical on every property except this one, i.e. the only reason
// Shopify kept them apart is the C&C stamp this feature controls.
function propertiesSignatureIgnoringLocation(properties) {
  const entries = Object.entries(properties || {})
    .filter(([key]) => key !== LINE_LOCATION_PROPERTY)
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

async function consolidateDuplicateVariants(items) {
  if (_consolidating) return;

  const byVariantAndProps = new Map();
  for (const item of items || []) {
    const key = `${item.variant_id}::${propertiesSignatureIgnoringLocation(item.properties)}`;
    if (!byVariantAndProps.has(key)) byVariantAndProps.set(key, []);
    byVariantAndProps.get(key).push(item);
  }
  const duplicates = [...byVariantAndProps.values()].filter((lines) => lines.length > 1);
  if (duplicates.length === 0) return;

  _consolidating = true;
  try {
    for (const lines of duplicates) {
      await queueCartMutation(() => mergeVariantLines(lines));
    }
  } finally {
    _consolidating = false;
  }
}

/**
 * Collapses every line for one variant onto the earliest-added line (first
 * in cart.items, matching the "stamped at add time" precedence used
 * elsewhere) by summing quantities onto it and zeroing the rest. The kept
 * line's own properties are left untouched — /cart/change.js only changes
 * properties when a `properties` key is explicitly sent.
 */
async function mergeVariantLines(lines) {
  const [keep, ...extra] = lines;

  const drawer = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  const sections = drawer?.getSectionsToRender?.().map((s) => s.id) || [];

  try {
    for (const line of extra) {
      const res = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({ id: line.key, quantity: 0 }),
      });
      if (!res.ok) return; // Leave as-is; the next cart load will retry.
    }

    const totalQty = lines.reduce((sum, l) => sum + l.quantity, 0);
    const res = await fetch(routes.cart_change_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        id: keep.key,
        quantity: totalQty,
        sections,
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) return;
    const data = await res.json();

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'cc-cart-inventory-indicator',
      cartData: data,
    });

    if (drawer?.renderContents) drawer.renderContents(data, { shouldOpen: false });
  } catch (err) {
    console.error('[cc-cart-inventory-indicator] duplicate-line merge failed:', err);
  }
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/**
 * Resolves after `ms`, or as soon as `signal` aborts — whichever comes first.
 * @param {number} ms
 * @param {AbortSignal} signal
 * @returns {Promise<void>}
 */
function wait(ms, signal) {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener('abort', done, { once: true });
  });
}

let _warehouseGid;

function getWarehouseGid() {
  if (_warehouseGid !== undefined) return _warehouseGid;
  const el = document.querySelector('[data-cc-store-selector-data]');
  if (!el) { _warehouseGid = null; return null; }
  try {
    const data = JSON.parse(el.textContent);
    _warehouseGid = data?.settings?.warehouse_gid || null;
  } catch {
    _warehouseGid = null;
  }
  return _warehouseGid;
}

function isWarehouseLocation(location) {
  return isWarehouseLocationUtil(location, getWarehouseGid());
}

let _storesData;

function getStoresData() {
  if (_storesData !== undefined) return _storesData;
  const el = document.querySelector('[data-cc-store-selector-data]');
  if (!el) { _storesData = []; return _storesData; }
  try {
    const data = JSON.parse(el.textContent);
    _storesData = Array.isArray(data?.stores) ? data.stores : [];
  } catch {
    _storesData = [];
  }
  return _storesData;
}

/**
 * Resolve a location's display name from the merchant-managed store list
 * (the same metaobject data that determines cluster/radius membership via
 * relevantGids) rather than the inventory API's own `location.name`, which
 * can drift from it (e.g. a location renamed in Shopify admin after the
 * store metaobject was set up). Falls back to the inventory location's own
 * name if no matching store entry is found.
 */
function resolveStoreName(location) {
  if (!location) return '';
  const meta = getStoresData().find((s) => isSameLocation(location, s.location_id, s.location_id));
  return meta?.name || location.name || '';
}

function getSelectedStoreId() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.selectedStoreGid || parsed?.selectedStoreId || null;
  } catch {
    return null;
  }
}

/**
 * Sum cart quantity across every registered line sharing the same variant.
 * A single variant can be split across multiple cart lines (e.g. a "buy X
 * get Y free" promotion), so a line's own quantity alone is not enough to
 * tell whether combined demand exceeds what the selected store has in
 * stock — each line in isolation can look "within capacity" while the
 * total already isn't.
 * @param {string} variantId
 * @returns {number}
 */
function getVariantCartTotal(variantId) {
  let total = 0;
  for (const el of Controller.instances) {
    if (el.dataset.variantId === variantId) {
      total += Number(el.dataset.cartQuantity) || 0;
    }
  }
  return total;
}

function getRelevantStoreGids() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.selectedStoreGid) return null;
    const gids = new Set([parsed.selectedStoreGid]);
    if (Array.isArray(parsed.nearbyStoreGids)) {
      for (const gid of parsed.nearbyStoreGids) gids.add(gid);
    }
    return gids;
  } catch {
    return null;
  }
}

// Country allow-list gate. Cached promise so all cart indicators share a
// single resolution. Resolves true when:
//   - the store selector data block is missing (CC disabled / no config), OR
//   - allowed_countries is empty (no restriction), OR
//   - geo-IP country is in the allow list.
let _countryAllowedPromise;
function getCountryAllowed() {
  if (_countryAllowedPromise) return _countryAllowedPromise;
  _countryAllowedPromise = (async () => {
    const el = document.querySelector('[data-cc-store-selector-data]');
    if (!el) return false;
    let allowedCountries = [];
    try {
      const data = JSON.parse(el.textContent);
      allowedCountries = (data?.settings?.allowed_countries || '')
        .split(',')
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean);
    } catch {
      return false;
    }
    try {
      return await isCountryAllowed(allowedCountries);
    } catch {
      return false;
    }
  })();
  return _countryAllowedPromise;
}

let _i18nCache;
function getI18n() {
  if (_i18nCache) return _i18nCache;
  const el = document.querySelector('cc-store-selector [data-cc-store-selector-i18n]');
  if (!el) { _i18nCache = {}; return _i18nCache; }
  try {
    _i18nCache = JSON.parse(el.textContent) || {};
  } catch {
    _i18nCache = {};
  }
  return _i18nCache;
}

// ---------------------------------------------------------------------------
// Cart-level controller (singleton)
// ---------------------------------------------------------------------------

const Controller = {
  instances: new Set(),
  _timer: null,
  _shouldBlock: false,
  _hasConflict: false,
  _submitGuardInstalled: false,

  register(el) {
    this.instances.add(el);
    // Re-apply previously known blocked state immediately so newly rendered
    // checkout buttons (after a cart drawer re-render) stay disabled while
    // this instance fetches its inventory.
    if (this._shouldBlock) this.applyToButtons(true);
    this.scheduleEvaluate();
  },

  unregister(el) {
    this.instances.delete(el);
    this.scheduleEvaluate();
  },

  notify() {
    this.scheduleEvaluate();
  },

  scheduleEvaluate() {
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this.evaluate(), 50);
  },

  evaluate() {
    const allInstances = [...this.instances];
    // getSummary() omits 'loading'/'remove' lines entirely, so on its own
    // this would treat "still checking" as "nothing wrong" and let checkout
    // through mid-revalidation (e.g. right after a bfcache-restored cart
    // kicks off a fresh stock check). Block on those states directly instead
    // of waiting for them to resolve into a confirmed problem.
    const isUnsettled = allInstances.some(
      (el) => el.state === 'loading' || el.state === 'remove',
    );
    const summaries = allInstances.map((el) => el.getSummary()).filter(Boolean);

    if (summaries.length === 0 && !isUnsettled) {
      this.applyConflict(false, false);
      return;
    }

    const hasInsufficient = isUnsettled || summaries.some((s) => s.state === 'insufficient');
    const hasConflict = this.detectConflict(summaries);
    this.applyConflict(hasConflict, hasInsufficient);
  },

  /**
   * Conflict: cart cannot be fulfilled by either (a) shipping online
   * (every line available at a warehouse) or (b) a single physical store
   * within the configured radius (selected store + nearby stores).
   *
   * Mixed origin stamps (different _cc_location_id values) are a conflict
   * only when online fulfilment is not possible — otherwise the order can
   * ship online regardless of which store each line was added under.
   */
  detectConflict(summaries) {
    // Online fulfilment available for every line → no conflict regardless
    // of where each line was originally added.
    const fulfillOnline = summaries.every((s) => s.hasWarehouse);
    if (fulfillOnline) return false;

    // Mixed origin stamps with no online fallback → cannot consolidate
    // into a single pickup order.
    const locationIds = new Set(
      summaries.map((s) => s.lineLocationId).filter(Boolean),
    );
    if (locationIds.size > 1) return true;

    // Look for at least one store within radius that stocks every line.
    const relevantGids = getRelevantStoreGids();

    const allStoreIds = new Set();
    for (const s of summaries) {
      for (const id of s.storeIds) {
        if (!relevantGids || relevantGids.has(id)) allStoreIds.add(id);
      }
    }

    for (const id of allStoreIds) {
      if (summaries.every((s) => s.storeIds.has(id))) return false;
    }

    return true;
  },

  applyConflict(hasConflict, hasInsufficient) {
    const shouldBlock = hasConflict || hasInsufficient;
    this._shouldBlock = shouldBlock;
    this._hasConflict = hasConflict;
    this.renderBanner(hasConflict);
    document.documentElement.classList.toggle(HTML_BLOCK_CLASS, shouldBlock);
    this.applyToButtons(shouldBlock);
    this.installSubmitGuard();
  },

  applyToButtons(shouldBlock) {
    for (const btn of document.querySelectorAll(CHECKOUT_SELECTORS)) {
      btn.toggleAttribute('disabled', shouldBlock);
      btn.classList.toggle('cc-checkout-disabled', shouldBlock);
      if (shouldBlock) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    }
  },

  /**
   * Belt-and-suspenders: even if the disabled state is somehow stripped from
   * the button (e.g. by a third-party script or a partial re-render), block
   * cart-form submissions while a conflict exists.
   */
  installSubmitGuard() {
    if (this._submitGuardInstalled) return;
    this._submitGuardInstalled = true;
    document.addEventListener(
      'submit',
      (event) => {
        if (!this._shouldBlock) return;
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!form.matches(CART_FORM_SELECTORS)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true,
    );
  },

  renderBanner(show) {
    const banners = document.querySelectorAll(CART_BANNER_SELECTOR);
    for (const banner of banners) {
      banner.classList.toggle('hidden', !show);
    }
  },

  showRemovedWarning() {
    const banners = document.querySelectorAll(CART_REMOVED_BANNER_SELECTOR);
    for (const banner of banners) {
      banner.classList.remove('hidden');
    }
  },

  hideRemovedWarning() {
    const banners = document.querySelectorAll(CART_REMOVED_BANNER_SELECTOR);
    for (const banner of banners) {
      banner.classList.add('hidden');
    }
  },
};

// ---------------------------------------------------------------------------
// Global listeners — re-fetch when store selection changes
// ---------------------------------------------------------------------------

function refetchAll() {
  Controller.hideRemovedWarning();
  for (const el of Controller.instances) {
    // Inventory data (per-location stock levels) does not change when the
    // store selection changes — only the interpretation changes.  Re-run
    // recompute() against the already-fetched locations instead of hitting
    // the proxy again, which triggers Cloudflare rate-limiting.
    if (el._locations) {
      el.recompute();
    } else {
      el.fetch();
    }
  }
  Controller.notify();
}

window.addEventListener('zippy:cc-store-selector:change', refetchAll);
window.addEventListener('storage', (e) => {
  if (e.key === STORAGE_KEY) refetchAll();
});

// Browser back/forward (e.g. returning from checkout) often restores the
// cart page from bfcache instead of re-parsing it, so connectedCallback
// never re-fires and stock/pickup data can go stale without any of the
// normal triggers noticing. Force a real re-fetch (not just recompute from
// cached locations) whenever that happens.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  Controller.hideRemovedWarning();
  for (const el of Controller.instances) {
    el.refetch();
  }
  Controller.notify();
});

// ---------------------------------------------------------------------------
// Add-to-cart stamping — inject _cc_location_id property at the form level.
//
// Two capture-phase listeners cover Horizon's add-to-cart paths:
//   1. `submit` on classic product forms (Horizon product-form.js).
//   2. `click` on PLP quick-add variant buttons (card-product-add-to-cart.js,
//      which calls onSubmitHandler directly without firing a submit event).
// JS-only callers (cc-store-availability, cc-store-selector) use
// addToCart() from @cc/utils which embeds the property in its JSON body.
// ---------------------------------------------------------------------------

installAddToCartStamp();

function installAddToCartStamp() {
  if (window.__ccCartStampInstalled) return;
  window.__ccCartStampInstalled = true;

  document.addEventListener('submit', onAddToCartSubmit, true);
  document.addEventListener('click', onQuickAddClick, true);
}

function onAddToCartSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!isAddToCartForm(form)) return;
  const variantId = form.querySelector('[name="id"]')?.value;
  stampForm(form, variantId);
}

function onQuickAddClick(event) {
  const button = event.target instanceof Element
    ? event.target.closest('.product-form__variant-button')
    : null;
  if (!button) return;
  const form = button.closest('form');
  // Read the variant straight off the button, not the form's `id` field —
  // this listener runs in the capture phase, before the button's own click
  // handler (bubble phase) writes the clicked variant into that field.
  if (form && isAddToCartForm(form)) stampForm(form, button.dataset.variantId);
}

function isAddToCartForm(form) {
  const action = form.getAttribute('action') || '';
  return /\/cart\/add(?:\.js)?(?:$|\?)/.test(action);
}

function stampForm(form, variantId) {
  // If this variant already has a line in the cart, match its existing
  // stamp exactly so Shopify's own add-to-cart merges into that line instead
  // of forking a second one (it only merges when properties are identical).
  const existingLocation = getExistingLineLocation(variantId);
  const locationToStamp = existingLocation !== undefined ? existingLocation : getSelectedStoreId();

  const input = form.querySelector(`input[name="${LINE_LOCATION_INPUT_NAME}"]`);
  if (!locationToStamp) {
    input?.remove();
    return;
  }
  if (input) {
    input.value = locationToStamp;
    return;
  }
  const newInput = document.createElement('input');
  newInput.type = 'hidden';
  newInput.name = LINE_LOCATION_INPUT_NAME;
  newInput.value = locationToStamp;
  form.appendChild(newInput);
}

// ---------------------------------------------------------------------------
// Custom element
// ---------------------------------------------------------------------------

class CCCartInventoryIndicator extends HTMLElement {
  static observedAttributes = [
    'data-product-id',
    'data-variant-id',
    'data-cart-quantity',
    'data-line-location-id',
  ];

  constructor() {
    super();
    this._abort = null;
    this._locations = null;
    this._removing = false;
    this.state = 'loading';
    this.storeIds = new Set();
    this.hasWarehouse = false;
  }

  connectedCallback() {
    getCountryAllowed().then((allowed) => {
      if (!this.isConnected) return;
      if (!allowed) {
        // Country outside the C&C allow-list → indicator is irrelevant.
        this._countryBlocked = true;
        this.innerHTML = '';
        this.classList.add('hidden');
        return;
      }
      Controller.register(this);
      this.fetch();
    });
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._abort = null;
    clearTimeout(this._loadTimer);
    if (!this._countryBlocked) Controller.unregister(this);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isConnected || oldValue === newValue) return;
    if (this._countryBlocked) return;

    if (name === 'data-product-id' || name === 'data-variant-id') {
      this._locations = null;
      this.fetch();
    } else if (name === 'data-cart-quantity' || name === 'data-line-location-id') {
      this.recompute();
      Controller.notify();
    }
  }

  refetch() {
    this._locations = null;
    this.fetch();
  }

  async fetch() {
    const productId = this.dataset.productId;
    const variantId = this.dataset.variantId;
    if (!productId || !variantId) return;

    // Mark unsettled for the duration of this fetch, not just the very first
    // one — a revalidation triggered later (store change, bfcache restore)
    // must also block checkout until it resolves, not keep showing the
    // previous (possibly stale) state as if it were still current.
    this.state = 'loading';

    this._abort?.abort();
    const abort = new AbortController();
    this._abort = abort;

    // Never let a slow or hanging proxy hold checkout hostage: if stock is
    // still unknown after the timeout, stop blocking. Data that arrives later
    // still applies below (and can re-block if it reveals a real problem).
    clearTimeout(this._loadTimer);
    this._loadTimer = setTimeout(() => this.markUnverified(), INVENTORY_LOAD_TIMEOUT_MS);

    for (let attempt = 0; ; attempt++) {
      // fetchInventory resolves null both on failure and on abort — an abort
      // means a newer fetch superseded this one, which is not a failure.
      const data = await fetchInventory(productId, abort.signal);
      if (abort.signal.aborted || !this.isConnected) return;

      const locations = extractLocations(data, variantId);
      if (locations) {
        clearTimeout(this._loadTimer);
        this._locations = locations;
        this.recompute();
        Controller.notify();
        return;
      }

      // Failed responses aren't cached by fetchInventory, so a retry really
      // goes back to the proxy.
      if (attempt >= INVENTORY_RETRY_DELAYS_MS.length) break;
      await wait(INVENTORY_RETRY_DELAYS_MS[attempt], abort.signal);
      if (abort.signal.aborted || !this.isConnected) return;
    }

    clearTimeout(this._loadTimer);
    this.markUnverified();
  }

  /**
   * Stop blocking checkout for a line whose stock can't be confirmed (the
   * inventory fetch failed or timed out). Mirrors the PDP indicator, which
   * doesn't block purchase on a failed fetch either.
   */
  markUnverified() {
    if (!this.isConnected || this.state !== 'loading') return;
    this.state = 'unverified';
    this.render(null);
    Controller.notify();
  }

  recompute() {
    const locations = this._locations;
    if (!locations) return;

    // A line's validation store is the one it was added under (stamped at
    // add-to-cart time). Fall back to the currently selected store only if
    // the line has no stamp (e.g. pre-existing carts before this feature).
    const lineStoreId = this.dataset.lineLocationId || null;
    const currentStoreId = getSelectedStoreId();
    const storeId = lineStoreId || currentStoreId;
    const cartQty = Number(this.dataset.cartQuantity) || 0;

    // Use both the metafield flag AND the theme-setting warehouse GID to
    // reliably distinguish warehouses from physical stores. A store whose
    // location has local pickup off can't fulfil the line, so it doesn't
    // count as a pickup source — the stamped store, the cluster, the
    // exclusive labels and the conflict check all follow from this list.
    const stores = locations.filter(
      (l) => !isWarehouseLocation(l) && l.available > 0 && hasLocalPickup(l),
    );
    const hasWarehouse = locations.some((l) => isWarehouseLocation(l) && l.available > 0);

    this.storeIds = new Set(stores.map((s) => s.id));
    this.hasWarehouse = hasWarehouse;

    // No stock anywhere → auto-remove (regardless of store context).
    if (stores.length === 0 && !hasWarehouse) {
      this.state = 'remove';
      this.render(null);
      this.autoRemove();
      return;
    }

    // No active store selection → fall back to warehouse-only mode.
    // C&C messages are only meaningful when a store is selected.
    if (!currentStoreId) {
      // Line stamped to a store but no warehouse fallback → cannot ship.
      if (lineStoreId && !hasWarehouse) {
        this.state = 'remove';
        this.render(null);
        this.autoRemove();
        return;
      }

      // With no store context, the warehouse is the only fulfilment path —
      // demand must still be capped to what it actually holds. Without this,
      // removing the preferred store (which skips every store-scoped check
      // below) would let a line's quantity exceed real stock.
      if (hasWarehouse) {
        const warehouseLocations = locations.filter((l) => isWarehouseLocation(l));
        const warehouseQty = warehouseLocations.reduce((sum, l) => sum + (l.available || 0), 0);
        const totalCartQty = getVariantCartTotal(this.dataset.variantId);
        if (totalCartQty > warehouseQty) {
          this.state = 'insufficient';
          this.render({ type: 'insufficient', qty: warehouseQty, storeName: resolveStoreName(warehouseLocations[0]) });
          if (totalCartQty === cartQty) {
            this.autoReduceQuantity(warehouseQty);
          }
          return;
        }
      }

      this.state = 'ok';
      this.render(null);
      return;
    }

    // From here, a store is selected. Treat the line's stamped store as
    // still relevant if it falls within the currently-selected store's
    // radius cluster (selected store + its nearby stores). This prevents
    // removing items added at store A when the user switches to nearby
    // store B that has overlapping radius.
    const relevantGids = getRelevantStoreGids();
    const lineStoreInCluster = lineStoreId && relevantGids?.has(lineStoreId);

    // Store change cleanup: if the customer picked a store outside the
    // line's cluster AND the line is not stocked at the newly-selected
    // store nor online-fulfillable, the line is no longer relevant.
    if (
      lineStoreId &&
      !isSameLocation({ id: lineStoreId }, currentStoreId, currentStoreId) &&
      !lineStoreInCluster &&
      !hasWarehouse &&
      !stores.some((s) => isSameLocation(s, currentStoreId, currentStoreId))
    ) {
      this.state = 'remove';
      this.render(null);
      this.autoRemove();
      return;
    }

    const selected = storeId
      ? stores.find((s) => isSameLocation(s, storeId, storeId))
      : null;
    const selectedQty = selected?.available ?? 0;
    const warehouseQty = locations
      .filter((l) => isWarehouseLocation(l))
      .reduce((sum, l) => sum + (l.available || 0), 0);
    const totalCartQty = getVariantCartTotal(this.dataset.variantId);

    // Shopify cannot split a single cart line's quantity across two
    // fulfillment methods (part picked up, part shipped) — but the
    // customer can always fulfil the *whole* line via whichever single
    // method covers it, so the real ceiling is the larger of the two, not
    // the selected store's stock alone.
    const maxPurchasable = Math.max(selectedQty, warehouseQty);

    // Genuinely insufficient: not even the better of the two methods can
    // cover the combined demand across every line of this variant — a
    // single line's own quantity can look "within capacity" while the
    // combined total already isn't.
    if (selected && totalCartQty > maxPurchasable) {
      this.state = 'insufficient';
      this.render({ type: 'insufficient', qty: maxPurchasable, storeName: resolveStoreName(selected) });
      // Only auto-correct the quantity for the simple single-line case —
      // when the variant spans multiple lines (e.g. a promo split) we can't
      // safely pick which one to trim without risking an inconsistent
      // price/promo state, so just warn instead.
      if (totalCartQty === cartQty) {
        this.autoReduceQuantity(maxPurchasable);
      }
      return;
    }

    // Fits within the warehouse, but exceeds what's available for pickup at
    // the selected store — the customer can buy the full quantity, they'll
    // just need to ship the part the store can't cover (or choose shipping
    // for the whole line at checkout).
    if (selected && totalCartQty > selectedQty) {
      this.state = 'partial-pickup';
      this.render({ type: 'partial-pickup', qty: selectedQty, storeName: resolveStoreName(selected) });
      return;
    }

    // Mirror of the above: fits within the selected store's pickup stock,
    // but exceeds what the warehouse alone could ship — the customer can
    // buy the full quantity, but only by picking it up in-store; choosing
    // to ship instead would leave part of it unfulfillable.
    if (selected && hasWarehouse && totalCartQty > warehouseQty) {
      this.state = 'pickup-only';
      this.render({ type: 'pickup-only', qty: selectedQty, storeName: resolveStoreName(selected) });
      return;
    }

    // Exclusivity messaging must reflect only what's reachable by this
    // customer's journey: the store they picked, any store within their
    // configured pickup radius (relevantGids), and the warehouse. Stock
    // sitting in an unrelated store outside that cluster is invisible to
    // them and must not influence store-exclusive / online-only messaging —
    // only what's *within reach* counts toward "exclusive". Whenever exactly
    // one in-cluster store carries the stock, the label is always the plain
    // "[Store] Click & Collect exclusive" copy — regardless of whether that
    // store happens to be the one currently selected — never the "not
    // available at X" phrasing (that framing is reserved for the PDP).
    const clusterStores = relevantGids
      ? stores.filter((s) => relevantGids.has(s.id))
      : stores.filter((s) => isSameLocation(s, storeId, storeId));

    const totalSources = clusterStores.length + (hasWarehouse ? 1 : 0);

    if (totalSources === 1) {
      if (hasWarehouse) {
        this.state = 'online-only';
        this.render({ type: 'online-only' });
        return;
      }

      this.state = 'store-exclusive';
      this.render({ type: 'store-exclusive', storeName: resolveStoreName(clusterStores[0]) });
      return;
    }

    this.state = 'ok';
    this.render(null);
  }

  render(message) {
    if (!message) {
      this.innerHTML = '';
      return;
    }
    const i18n = getI18n();
    const { iconClass, className, text } = this.buildMessage(message, i18n);
    this.innerHTML =
      `<span class="${LINE_LABEL_CLASS} ${className} flex items-center gap-1 mt-2 text-xs">` +
      `<span class="${LINE_LABEL_CLASS}__icon ${iconClass}" aria-hidden="true"></span>` +
      `<span class="text-xs">${escapeHtml(text)}</span>` +
      `</span>`;
  }

  buildMessage(message, i18n) {
    if (message.type === 'insufficient') {
      const text = (i18n.cart_line_insufficient_stock || '')
        .replace('[QTY]', String(message.qty))
        .replace('[STORE]', message.storeName || '');
      return {
        iconClass: `${LINE_LABEL_CLASS}__icon--warning`,
        className: `${LINE_LABEL_CLASS}--warning`,
        text,
      };
    }
    if (message.type === 'partial-pickup') {
      const text = (i18n.cart_line_partial_pickup || '')
        .replace('[QTY]', String(message.qty))
        .replace('[STORE]', message.storeName || '');
      return {
        iconClass: `${LINE_LABEL_CLASS}__icon--warning`,
        className: `${LINE_LABEL_CLASS}--neutral`,
        text,
      };
    }
    if (message.type === 'pickup-only') {
      const text = (i18n.cart_line_pickup_only || '')
        .replace('[QTY]', String(message.qty))
        .replace('[STORE]', message.storeName || '');
      return {
        iconClass: `${LINE_LABEL_CLASS}__icon--warning`,
        className: `${LINE_LABEL_CLASS}--warning`,
        text,
      };
    }
    if (message.type === 'store-exclusive') {
      const text = (i18n.cart_line_store_exclusive || '').replace(
        '[STORE]',
        message.storeName || '',
      );
      return {
        iconClass: `${LINE_LABEL_CLASS}__icon--store`,
        className: `${LINE_LABEL_CLASS}--store`,
        text,
      };
    }
    return {
      iconClass: `${LINE_LABEL_CLASS}__icon--globe`,
      className: `${LINE_LABEL_CLASS}--online`,
      text: i18n.cart_line_online_only || '',
    };
  }

  async autoReduceQuantity(maxQty) {
    if (this._reducing) return;
    this._reducing = true;

    const variantId = this.dataset.variantId;
    const lineKey = this.dataset.lineKey || variantId;
    try {
      const drawer =
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-notification');
      const sections = drawer?.getSectionsToRender?.().map((s) => s.id) || [];

      const body = JSON.stringify({
        id: lineKey,
        quantity: maxQty,
        sections,
        sections_url: window.location.pathname,
      });

      const res = await queueCartMutation(() => {
        if (!this.isConnected) return null;
        return fetch(routes.cart_change_url, { ...fetchConfig(), body });
      });
      if (!res) return; // Element disconnected before mutation executed
      const data = await res.json();

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cc-cart-inventory-indicator',
        cartData: data,
      });

      if (drawer?.renderContents) drawer.renderContents(data, { shouldOpen: false });
    } catch (err) {
      console.error('[cc-cart-inventory-indicator] auto-reduce failed:', err);
    } finally {
      this._reducing = false;
    }
  }

  async autoRemove() {
    if (this._removing) return;
    this._removing = true;

    const lineKey = this.dataset.lineKey || this.dataset.variantId;
    try {
      const drawer =
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-notification');
      const sections = drawer?.getSectionsToRender?.().map((s) => s.id) || [];

      const body = JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections,
        sections_url: window.location.pathname,
      });

      const res = await queueCartMutation(() => {
        if (!this.isConnected) return null;
        return fetch(routes.cart_change_url, { ...fetchConfig(), body });
      });
      if (!res) return; // Element disconnected before mutation executed
      if (!res.ok) throw new Error(`cart/change returned ${res.status}`);
      const data = await res.json();

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cc-cart-inventory-indicator',
        cartData: data,
      });

      if (drawer?.renderContents) drawer.renderContents(data, { shouldOpen: false });

      Controller.showRemovedWarning();
    } catch (err) {
      console.error('[cc-cart-inventory-indicator] auto-remove failed:', err);
      // Don't leave the line stuck in 'remove', which blocks checkout with no
      // message — release the block; Shopify checkout reports the real stock.
      if (this.isConnected && this.state === 'remove') {
        this.state = 'unverified';
        Controller.notify();
      }
    } finally {
      this._removing = false;
    }
  }

  getSummary() {
    // 'unverified' lines have no trustworthy stock data, so they take no part
    // in conflict detection (and, unlike 'loading'/'remove', don't block).
    if (this.state === 'loading' || this.state === 'remove' || this.state === 'unverified') {
      return null;
    }
    return {
      state: this.state,
      storeIds: this.storeIds,
      hasWarehouse: this.hasWarehouse,
      lineLocationId: this.dataset.lineLocationId || null,
    };
  }
}

if (!customElements.get('cc-cart-inventory-indicator')) {
  customElements.define('cc-cart-inventory-indicator', CCCartInventoryIndicator);
}
